// Konsolidierte SOLL-Matrix einer Konzern-Gruppe (read-only): aggregiert die
// aktiven Einzelplaene, mit netto/brutto/Elimination-Umschalter. Laedt selbst
// ueber GET /liquidity-planning/consolidation/:groupId/matrix.
// Wiederverwendet von KonzernMatrixPage und PlanEditorPage (Konzern-Scope).

import { useEffect, useMemo, useState } from 'react'
import { apiClient } from '@/lib/api.ts'
import {
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
} from '@/components/ui'
import {
  EUR,
  MONTHS,
  SECTION_LABEL,
  SECTION_ORDER,
  VIEW_LABEL,
  rowValues,
  yearAggregate,
  type MatrixResponse,
  type MatrixRow,
  type ViewMode,
} from './konzern-shared.ts'

export function ConsolidatedMatrix({ groupId, reloadToken = 0 }: { groupId: number; reloadToken?: number }) {
  const [matrix, setMatrix] = useState<MatrixResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('net')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    apiClient
      .get<MatrixResponse>(`/liquidity-planning/consolidation/${groupId}/matrix`)
      .then((res) => { if (!cancelled) setMatrix(res.data) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Matrix konnte nicht geladen werden') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [groupId, reloadToken])

  const rowsBySection = useMemo(() => {
    const map: Record<string, MatrixRow[]> = {}
    for (const sec of SECTION_ORDER) map[sec] = []
    for (const r of matrix?.matrix ?? []) (map[r.section] ??= []).push(r)
    for (const sec of SECTION_ORDER) map[sec]!.sort((a, b) => a.sortOrder - b.sortOrder)
    return map
  }, [matrix])

  if (error) return <ErrorState title="Fehler" description={error} />

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-3 flex-wrap">
          {matrix && (
            <div className="flex flex-wrap gap-1">
              {matrix.members.map((m) => (
                <StatusBadge
                  key={m.companyKey}
                  status={m.companyKey}
                  variant={m.found ? 'success' : 'warning'}
                  label={`${m.companyKey}${m.found ? '' : ' (kein aktiver Plan)'}`}
                />
              ))}
            </div>
          )}
          {matrix?.eliminate && (
            <div className="ml-auto w-56">
              <SelectRoot value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="net">{VIEW_LABEL.net}</SelectItem>
                  <SelectItem value="gross">{VIEW_LABEL.gross}</SelectItem>
                  <SelectItem value="elim">{VIEW_LABEL.elim}</SelectItem>
                </SelectContent>
              </SelectRoot>
            </div>
          )}
        </div>
      </Card.Header>
      <Card.Body className="p-0 overflow-x-auto">
        {loading ? (
          <LoadingState label="Konsolidiere …" />
        ) : matrix ? (
          <table className="w-full text-small border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left p-2 sticky left-0 bg-white min-w-64">Position</th>
                {MONTHS.map((mo) => (
                  <th key={mo} className="text-right p-2 whitespace-nowrap">{mo}</th>
                ))}
                <th className="text-right p-2 font-semibold">Jahr</th>
              </tr>
            </thead>
            <tbody>
              {SECTION_ORDER.flatMap((sec) => {
                const rows = rowsBySection[sec] ?? []
                if (rows.length === 0) return []
                return [
                  <tr key={`sec-${sec}`} className="bg-gray-50">
                    <td colSpan={14} className="p-2 font-semibold text-gray-700">{SECTION_LABEL[sec] ?? sec}</td>
                  </tr>,
                  ...rows.map((r) => {
                    const vals = rowValues(r, viewMode)
                    return (
                      <tr key={r.code} className={`border-b border-gray-100 ${r.isAggregate ? 'font-semibold bg-gray-50/50' : ''}`}>
                        <td className={`p-2 sticky left-0 bg-white ${r.parentCode ? 'pl-6 text-gray-600' : ''}`}>{r.label}</td>
                        {vals.map((v, i) => (
                          <td key={i} className="text-right p-2 tabular-nums">{v !== 0 ? EUR.format(v) : '–'}</td>
                        ))}
                        <td className="text-right p-2 font-semibold tabular-nums">{EUR.format(yearAggregate(r.code, vals))}</td>
                      </tr>
                    )
                  }),
                ]
              })}
            </tbody>
          </table>
        ) : (
          <EmptyState title="Keine Daten" description="Für diese Gruppe konnten keine Werte konsolidiert werden." />
        )}
      </Card.Body>
    </Card>
  )
}
